import * as React from 'react';
// Make sure that the import path is correct for your project.
import PostCardComponent from './PostCardComponent'; 
import { Button } from '@progress/kendo-react-buttons';
import { Trans } from 'react-i18next';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { SearchMissingsService } from 'services/Missings/SearchMissingsService';
import { useNotification } from 'contexts/NotificationContext';
import { ConfirmModal } from 'components/ConfirmModal';
import { t } from 'i18next';

// Define an interface for the single element (Post) for clarity.
export interface Post {
    DocumentId: number;
    DocumentProviderId: string;
    ImageUrl?: string;
    Title?: string;
    Source?: string;
    Content?: string;
    Status?: number;
    UniqueId?: string;
    Url?: string;
    PublishDate?: Date;
}

interface PostCardGridProps {
    PeriodStartDate: string;
    PeriodEndDate: string;
    posts: Post[];
}

const DEBOUNCE_DELAY = 800;

const PostCardGrid = ( props : PostCardGridProps) => {
    
    const searchMissingsService = new SearchMissingsService();
    const [show, setShow] = React.useState(false);
    const notification = useNotification();
    const gridStyle: React.CSSProperties = {
        display: 'flex',
        flexWrap: 'wrap', // allow wrapping to new lines
        gap: '20px',      // space between cards
        padding: '20px',
        justifyContent: 'flex-start' // align cards to the start
    };
    // Each card has a maxWidth of 350px. To ensure 4 items per row, 
    // we make sure the container provides space for 4 x ~350px + gap.

    // Filter out posts with duplicate documentId
    const [uniquePosts, setUniquePosts] = React.useState<Post[]>(props.posts);
    const [textSearch, setTextSearch] = React.useState<string>("");
    const [showPostsLoading, setShowPostsLoading] = React.useState<boolean>(false);
    const postsToImportCount = React.useMemo(
        () => uniquePosts.reduce((acc, post) => (post.Status === 2 ? acc + 1 : acc), 0),
        [uniquePosts]
    );
    const message = t("dataLabeling.missings.confirmImportAllModalMessage", {
        count: postsToImportCount
      });
      
    React.useEffect(() => {
        const handler = setTimeout(() => {
            const search = textSearch.trim().toLowerCase();
            if (!search) {
                setUniquePosts(props.posts);
                return;
            }
            
            setUniquePosts(
                props.posts.filter((post) => {
                    const content = typeof post.Content === "string" ? post.Content.toLowerCase() : "";
                    const title = typeof post.Title === "string" ? post.Title.toLowerCase() : "";
                    const uniqueId = typeof post.UniqueId === "string" ? post.UniqueId.toLowerCase() : "";
                    const status = typeof getStatusFromCode(post.Status) === "string" ? getStatusFromCode(post.Status)!.toLowerCase() : "";
                    return content.includes(search) || title.includes(search) || uniqueId.includes(search) || status.startsWith(search.replace(/\s+/g, ''));
                })
            );
        }, DEBOUNCE_DELAY);

        // Cleanup if textSearch changes before timeout completes
        return () => clearTimeout(handler);
    }, [textSearch, props.posts, DEBOUNCE_DELAY]);

    const getStatusFromCode = (statusCode: number | undefined) => {
        switch(statusCode) {
          case 0: return 'CompleteMissing';
          case 1: return 'Imported';
          case 2: return 'NotImported';
          case 3: return 'NotInPanel';
        }
      };

    const ImportedRefresh = (dictionary: { [key: string]: number }) => {
        Object.entries(dictionary).forEach(([key, value]) => {
            const postIndex = uniquePosts.findIndex(post => post.UniqueId === key);
            if (postIndex !== -1) {
                const updatedPosts = [...uniquePosts];
                updatedPosts[postIndex].DocumentId = value;
                updatedPosts[postIndex].Status = 1; // Set status to Imported
                setUniquePosts(updatedPosts);
            }
        });
    };

    const importDocument = async () => {
        setShowPostsLoading(true);
        await searchMissingsService
        .ImportMissingDocuments({
            ImportAll: true,
            UniqueIds: [],
            PeriodStartDate: props.PeriodStartDate,
            PeriodEndDate: props.PeriodEndDate
        })
        .then((response: any) => {
            console.log(`All documents were imported correctly.`);
            notification.showNotification("All documents were imported correctly.");
            ImportedRefresh(response);
            setShowPostsLoading(false);
        })
        .catch((error: any) => {
          console.error("Error importing: ", error);
          setShowPostsLoading(false);
        });
    };

    const handleConfirm = () => {
        importDocument();
        setShow(false);
    }

    const handleClose = () => {
        setShow(false);
      }

    const openConfirmModal = () => {
        setShow(true);
    }

    return (
        <div style={gridStyle}>
            <h5 className="title">
                <Trans>dataLabeling.missings.searchText</Trans>
            </h5>
            <div style={{flex: '0 0 100%', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <fieldset style={{ marginRight: '10px' }}>
                    <div className="subtitle">Text</div>
                    <Input
                        placeholder="Type text to search..."
                        onChange={(e: InputChangeEvent) => setTextSearch(e.value)}
                        value={textSearch ?? ""}
                    />
                </fieldset>
                <Button
                    onClick={openConfirmModal}
                    disabled={postsToImportCount === 0}
                >
                    <Trans>dataLabeling.missings.importAll</Trans>
                </Button>
            </div>
            {(showPostsLoading &&  
              <div className="d-flex justify-content-center align-items-center" style={{ width:'100%', height: '200px' }}>
                <span className="k-icon k-i-loading k-spin" style={{ fontSize: "48px" }}></span>
              </div>
            )}
            {(!showPostsLoading  && 
                <>
                {(uniquePosts.map((post) => (
                    <div 
                        key={post.UniqueId ?? post.DocumentId} 
                        // 24% to grant 4 posts per row, leaving space for gap between them 
                        style={{ flex: '0 0 23%', minWidth: '250px' }} 
                    >
                        <PostCardComponent
                            documentProviderId={post.DocumentProviderId}
                            documentId ={post.DocumentId}
                            periodStartDate={props.PeriodStartDate}
                            periodEndDate={props.PeriodEndDate}
                            imageUrl={post.ImageUrl}
                            title={post.Title}
                            source={post.Source}
                            content={post.Content}
                            status={getStatusFromCode(post.Status)}
                            uniqueId={post.UniqueId}
                            url={post.Url}
                            publishDate={post.PublishDate}
                
                        />
                    </div>
                )))}
                </>
            )}
            {show && (
                <ConfirmModal show={show} setShow={setShow} handleConfirm={handleConfirm} title="dataLabeling.missings.confirmImportAllModalTitle" message={message} onClose={handleClose} />
            )}
        </div>
            
    );
}

export default PostCardGrid;